/**
 * Toast - Notification toast system
 *
 * Usage:
 * // In your app root
 * <ToastProvider>
 *   <App />
 * </ToastProvider>
 *
 * // In any component
 * const { addToast } = useToast();
 * addToast({ message: 'Scene activated', variant: 'success' });
 */

import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';
import { createContext, useCallback, useContext, useState, useRef, useEffect } from 'react';

import { cn } from '../utils';

export interface Toast {
  id: string;
  message: string;
  variant?: 'success' | 'error' | 'info' | 'warning';
  duration?: number;
}

interface ToastContextValue {
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastIdRef = useRef(0);

  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = String(++toastIdRef.current);
    setToasts((prev) => [...prev, { ...toast, id }]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ addToast, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
}

function ToastContainer({ toasts, onRemove }: { toasts: Toast[]; onRemove: (id: string) => void }) {
  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onRemove={onRemove} />
      ))}
    </div>
  );
}

const variantStyles = {
  success: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 ring-emerald-500/20',
  error: 'bg-red-500/10 text-red-700 dark:text-red-400 ring-red-500/20',
  info: 'bg-blue-500/10 text-blue-700 dark:text-blue-400 ring-blue-500/20',
  warning: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 ring-amber-500/20',
};

const variantIcons = {
  success: CheckCircle,
  error: AlertCircle,
  info: Info,
  warning: AlertCircle,
};

function ToastItem({ toast, onRemove }: { toast: Toast; onRemove: (id: string) => void }) {
  const { id, message, variant = 'info', duration = 4000 } = toast;
  const Icon = variantIcons[variant];

  useEffect(() => {
    const timer = setTimeout(() => {
      onRemove(id);
    }, duration);

    return () => clearTimeout(timer);
  }, [id, duration, onRemove]);

  return (
    <div
      role="alert"
      className={cn(
        'flex items-center gap-3 rounded-lg px-4 py-3 shadow-lg ring-1',
        'bg-white dark:bg-zinc-900',
        'animate-in slide-in-from-right-full duration-300',
        variantStyles[variant]
      )}
    >
      <Icon className="h-5 w-5 flex-shrink-0" />
      <span className="text-sm font-medium">{message}</span>
      <button
        onClick={() => onRemove(id)}
        className="ml-2 rounded p-0.5 transition-colors hover:bg-black/5 dark:hover:bg-white/10"
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
